import type { ReactNode } from 'react'
import {
  ArrowSync20Regular,
  BrainCircuit20Regular,
  CheckmarkCircle16Filled,
  Copy20Regular,
  DocumentSearch20Regular,
  Eye20Regular,
  FolderOpen20Regular,
  History20Regular,
  NetworkCheck20Regular,
  ScanObject20Regular,
  Search20Regular,
  ShieldCheckmark20Regular,
  Tag20Regular,
} from '@fluentui/react-icons'
import { DetailSection, NextPage, PageHero, SubNav } from '../components/PageChrome'
import { Reveal } from '../components/Reveal'
import { SpotlightCard } from '../components/Primitives'

type Feature = { icon: ReactNode; title: string; text: string; details: string[] }

const groups: { id: string; index: string; title: string; lead: string; items: Feature[] }[] = [
  {
    id: 'discovery',
    index: '01',
    title: 'Search and discovery',
    lead: 'Find the right document, or the right paragraph inside it, without knowing where it was filed.',
    items: [
      {
        icon: <Search20Regular />,
        title: 'Unified search',
        text: 'One query runs against the local workstation and, in managed mode, the policy-approved office catalog at the same time.',
        details: ['Filename and path matching', 'Metadata and tag filters', 'Merged local + server ranking', 'Unavailable sources are reported, not hidden'],
      },
      {
        icon: <BrainCircuit20Regular />,
        title: 'Semantic passages',
        text: 'Embeddings retrieve passages by meaning when the document phrases something differently — including across English and Greek.',
        details: ['SQLite vectors on the workstation', 'pgvector with HNSW on the server', 'Page-level source attribution', 'recall@10 ≥ 0.95 gate'],
      },
      {
        icon: <DocumentSearch20Regular />,
        title: 'Advanced query',
        text: 'Combine literal text, meaning and structured filters when a search turns into an investigation.',
        details: ['Date, size and file-type filters', 'Owner, source and location', 'Tags and categories', 'Hybrid relevance scoring'],
      },
    ],
  },
  {
    id: 'ingestion',
    index: '02',
    title: 'Ingestion and understanding',
    lead: 'Turn folders that already exist into a catalog that can be inspected and trusted.',
    items: [
      {
        icon: <ScanObject20Regular />,
        title: 'English and Greek OCR',
        text: 'A bundled Tesseract runtime makes scanned PDFs and images searchable without sending pages anywhere.',
        details: ['eng + ell traineddata shipped', 'Processing stays local', 'Per-page status and retries', 'Failure evidence kept in File Details'],
      },
      {
        icon: <FolderOpen20Regular />,
        title: 'In-place indexing',
        text: 'Configured roots are watched continuously. Nothing is uploaded and nothing is duplicated into a second store.',
        details: ['Incremental file watching', 'Hash-based change detection', 'Rename and move tracking', 'Watcher overflow recovery'],
      },
      {
        icon: <Copy20Regular />,
        title: 'Duplicates and clean-up',
        text: 'Identify byte-identical and near-identical copies across roots before deciding what an office actually needs to keep.',
        details: ['Content hashing', 'Grouped review', 'Recoverable quarantine', 'Nothing deleted without confirmation'],
      },
    ],
  },
  {
    id: 'operations',
    index: '03',
    title: 'Context, governance and operations',
    lead: 'Move from finding information to managing it, without giving up control of the filesystem.',
    items: [
      {
        icon: <Tag20Regular />,
        title: 'Tags and business context',
        text: 'Enrich documents with controlled categories and metadata that never change how staff open their working files.',
        details: ['Manual and suggested tags', 'Parsed document metadata', 'Classification evidence', 'Saved searches and collections'],
      },
      {
        icon: <Eye20Regular />,
        title: 'File Details workspace',
        text: 'Content, metadata, tags, OCR state, semantic passages, permissions and history for a file in one view.',
        details: ['Parsed and OCR text', 'Matching search passages', 'Processing errors', 'Complete activity timeline'],
      },
      {
        icon: <ShieldCheckmark20Regular />,
        title: 'Organization planning',
        text: 'Model the folder structure the business wants, simulate the move, and approve it as an immutable plan.',
        details: ['Versioned business ontology', 'Supplied or discovered structures', 'Conflict and ACL simulation', 'Journal, verification and rollback'],
      },
      {
        icon: <History20Regular />,
        title: 'Safe file operations',
        text: 'Move, copy, rename, quarantine and restore run through confirmation, revalidation and a recoverable journal.',
        details: ['Pre-flight ACL revalidation', 'Recoverable quarantine', 'Durable operation journal', 'Audit record per action'],
      },
      {
        icon: <NetworkCheck20Regular />,
        title: 'Office administration',
        text: 'Administrators manage users, enrolled workstations, shared roots, policy and indexing health inside the same application.',
        details: ['Live endpoint status', 'Shared root configuration', 'Visibility policy', 'Automation review queue'],
      },
      {
        icon: <ArrowSync20Regular />,
        title: 'Managed synchronization',
        text: 'Endpoints exchange only policy-approved catalog fields, with signed envelopes, cursors and durable conflict handling.',
        details: ['Signed request envelopes', 'Cursor-based resume', 'Offline reconciliation', 'Per-device admission limits'],
      },
    ],
  },
]

const matrix: [string, boolean, boolean][] = [
  ['Local full-text and metadata search', true, true],
  ['English and Greek OCR', true, true],
  ['Local semantic search', true, true],
  ['File Details, tags and history', true, true],
  ['Duplicate detection and safe operations', true, true],
  ['Shared file-server indexing', false, true],
  ['Endpoint catalog search across the office', false, true],
  ['Central users, roles and policy', false, true],
  ['Governance planning and approvals', false, true],
]

export function Features() {
  return (
    <main id="main">
      <PageHero
        kicker="Product features"
        title="Everything Libris does, in one place."
        lead="Search, OCR, semantic retrieval, file management, information governance and office administration — delivered by one desktop application and one background agent."
        specs={[
          ['Applications', 'Desktop · Agent · Server'],
          ['Content languages', 'English · Ελληνικά'],
          ['Local catalog', 'SQLite · FTS5 · vectors'],
          ['Install', 'Single signed MSI'],
        ]}
      />

      <SubNav items={groups.map(group => ({ id: group.id, label: group.title }))} />

      {groups.map(group => (
        <DetailSection key={group.id} id={group.id} index={group.index} title={group.title} lead={group.lead}>
          <div className="card-grid">
            {group.items.map((item, index) => (
              <SpotlightCard key={item.title} index={index % 3}>
                <span className="card-icon">{item.icon}</span>
                <h3>{item.title}</h3>
                <p className="body">{item.text}</p>
                <ul className="card-points">
                  {item.details.map(detail => (
                    <li key={detail}>
                      <CheckmarkCircle16Filled />
                      {detail}
                    </li>
                  ))}
                </ul>
              </SpotlightCard>
            ))}
          </div>
        </DetailSection>
      ))}

      <DetailSection
        id="availability"
        index="04"
        title="Availability by deployment mode"
        lead="Managed hybrid is a superset. Enrolling a workstation adds office-wide capability and never removes anything it had on its own."
        tinted
      >
        <Reveal className="matrix" variant="up">
          <div className="matrix-row is-head mono">
            <span>Capability</span>
            <span>Standalone local</span>
            <span>Managed hybrid</span>
          </div>
          {matrix.map(([label, local, hybrid], index) => (
            <div className="matrix-row" key={label} style={{ ['--i' as string]: index }}>
              <span>{label}</span>
              <span className={local ? 'is-yes' : 'is-no'}>
                {local ? <CheckmarkCircle16Filled /> : <i />}
                <em>{local ? 'Included' : 'Server required'}</em>
              </span>
              <span className={hybrid ? 'is-yes' : 'is-no'}>
                <CheckmarkCircle16Filled />
                <em>Included</em>
              </span>
            </div>
          ))}
        </Reveal>
      </DetailSection>

      <section className="band-tight">
        <div className="shell">
          <NextPage href="/infrastructure" label="Next" title="How the pieces fit together" />
        </div>
      </section>
    </main>
  )
}
